import { Record, Map } from "immutable";

import { IDsp, Dsp } from "@dsps/models";
import { IAlbum, Album, ILabel, Label, MusicNormalisedEntities } from "@music/models";
import { TranslatableMap } from "@localisation/models";
import { RestModel, RestRecord, InnerStateWithPagination, StateError } from "@orm/models";

export interface Filters {
    types: string[];
    statuses: string[];
    dspIds: number[];
    albumIds: number[];
}

export interface DeliveryInternalState extends InnerStateWithPagination {
    entities: DeliveryNormalisedEntities;
    list: string[];
    filters: Filters;
    requests: {
        indexLastLoaded: Date,
        indexError: StateError,
    };
}

export interface DeliveryState {
    deliveries: DeliveryInternalState;
}

export interface IDelivery extends RestModel {
    id?: string;
    wataJobId?: number;
    albumId?: number;
    upc?: string;
    labelId?: number;
    output?: string;
    fileStructure?: string;
    dspId?: number;
    createdAt?: Date;
    updatedAt?: Date;
    // Relationships
    batch?: string | IBatch;
    type?: string | IType;
    currentStatus?: string | IStatus;
    statuses?: string[] | IStatus[];
    album?: IAlbum;
    label?: ILabel;
    dsp?: IDsp;
}

export interface IBatch {
    name?: string;
    fileStructure?: string;
}

export interface IType {
    name?: string;
    label?: TranslatableMap;
    description?: string;
}

export interface IStatus {
    type?: IStatusType;
    occurredAt?: Date;
}

export interface IStatusType {
    name?: string;
    label?: TranslatableMap;
}

const DeliveryRecord = RestRecord({
    id: undefined,
    wataJobId: undefined,
    albumId: undefined,
    upc: undefined,
    labelId: undefined,
    output: undefined,
    fileStructure: undefined,
    dspId: undefined,
    createdAt: undefined,
    updatedAt: undefined,
    batch: undefined,
    type: undefined,
    currentStatus: undefined,
    statuses: undefined,
    album: undefined,
    label: undefined,
    dsp: undefined,
});

const BatchRecord = Record({
    name: undefined,
    fileStructure: undefined,
});

const TypeRecord = Record({
    name: undefined,
    label: undefined,
    description: undefined,
});

const StatusRecord = Record({
    type: undefined,
    occurredAt: undefined,
});

const StatusTypeRecord = Record({
    name: undefined,
    label: undefined,
});

export class Delivery extends DeliveryRecord implements IDelivery {
    public static RELATIONS = [
        "batch",
        "type",
        "currentStatus",
        "statuses",
        "album",
        "label",
    ];

    public id: string;
    public wataJobId: number;
    public albumId: number;
    public upc: string;
    public labelId: number;
    public output: string;
    public fileStructure: string;
    public dspId: number;
    public createdAt: Date;
    public updatedAt: Date;
    // Relationships
    public batch: string | Batch;
    public type: string | Type;
    public currentStatus: string | Status;
    public statuses: string[] | Status[];
    public album: Album;
    public label: Label;
    public dsp: Dsp;

    public getViewUrl(): string {
        return this.id ? "/deliveries/view/" + this.id : null;
    }
}

export class Batch extends BatchRecord implements IBatch {
    public name: string;
    public fileStructure: string;
}

export class Type extends TypeRecord implements IType {
    public name: string;
    public label: TranslatableMap;
    public description: string;
}

export class Status extends StatusRecord implements IStatus {
    public type: StatusType;
    public occurredAt: Date;
}

export class StatusType extends StatusTypeRecord implements IStatusType {
    public name: string;
    public label: TranslatableMap;
}

export interface DeliveryNormalisedEntities extends MusicNormalisedEntities {
    deliveries?: Map<string, Delivery>;
    deliveryBatches?: Map<string, Batch>;
    deliveryTypes?: Map<string, Type>;
    deliveryStatuses?: Map<string, Status>;
    deliveryStatusTypes?: Map<string, StatusType>;
}
